import React from 'react';
import {
    CheckCircleIcon,
    ExclamationTriangleIcon,
    CalendarIcon
} from '@heroicons/react/24/outline';

const ClassScheduleExpanded = ({
    venueClass,
    allDates,
    bulkSelectedDates,
    onDateToggle,
    onClearSelection,
    onBulkBooking,
    getClassSelectedDates,
    showUserBookings = true
}) => {
    const classSelectedDates = getClassSelectedDates(venueClass.id);
    const totalCost = classSelectedDates.length * venueClass.price;

    return (
        <div className="p-6 bg-gray-800/30">
            {/* Header with Instructions */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 gap-4">
                <div>
                    <h4 className="text-lg font-bold text-white mb-1">Select Your Dates</h4>
                    <p className="text-sm text-gray-400">
                        Choose the sessions you want to book • Click dates to select
                    </p>
                </div>
                {classSelectedDates.length > 0 && (
                    <div className="flex items-center gap-3">
                        <div className="text-right">
                            <div className="text-sm text-gray-400">
                                {classSelectedDates.length} session{classSelectedDates.length !== 1 ? 's' : ''}
                            </div>
                            <div className="text-lg font-bold text-emerald-400">
                                £{totalCost.toFixed(2)}
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Action Buttons */}
            {classSelectedDates.length > 0 && (
                <div className="flex flex-col sm:flex-row gap-3 mb-6">
                    <button
                        onClick={() => onClearSelection(venueClass.id)}
                        className="flex-1 px-6 py-3 bg-gray-600 hover:bg-gray-700 text-white rounded-lg font-medium transition-colors"
                    >
                        Clear Selection
                    </button>
                    <button
                        onClick={() => onBulkBooking(venueClass.id)}
                        className="flex-1 px-6 py-3 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-medium transition-colors flex items-center justify-center"
                    >
                        <CalendarIcon className="h-5 w-5 mr-2" />
                        Book {classSelectedDates.length} Session{classSelectedDates.length !== 1 ? 's' : ''} - £{totalCost.toFixed(2)}
                    </button>
                </div>
            )}


            {/* Available Dates Grid */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-3 mb-6">
                {allDates.map((date, index) => {
                    const dateString = date.toISOString().split('T')[0];
                    const existingBooking = venueClass.userClasses?.find(
                        b => {
                            const bookingDate = new Date(b.paymentDate);
                            const bookingDateString = bookingDate.toISOString().split('T')[0];
                            return bookingDateString === dateString;
                        }
                    );
                    const isBooked = showUserBookings && existingBooking && existingBooking.hasPaid;
                    const isPending = showUserBookings && existingBooking && !existingBooking.hasPaid;
                    const isSelected = bulkSelectedDates.includes(`${venueClass.id}-${date.toISOString()}`);

                    return (
                        <div
                            key={index}
                            className={`p-3 rounded-lg border cursor-pointer transition-all ${
                                isBooked
                                    ? 'bg-green-900/20 border-green-600 text-gray-300 cursor-default'
                                    : isPending && isSelected
                                    ? 'bg-emerald-600 border-emerald-500 text-white transform scale-105 shadow-lg'
                                    : isPending
                                    ? 'bg-yellow-900/20 border-yellow-600 text-gray-300 hover:border-yellow-500 hover:bg-yellow-900/30'
                                    : isSelected
                                    ? 'bg-emerald-600 border-emerald-500 text-white transform scale-105 shadow-lg'
                                    : 'bg-gray-800 border-gray-600 text-gray-300 hover:border-emerald-500 hover:bg-gray-700'
                            }`}
                            onClick={() => !isBooked && onDateToggle(venueClass.id, date)}
                        >
                            <div className="text-center">
                                <div className="font-semibold text-sm mb-1">
                                    {date.toLocaleDateString('en-GB', {
                                        weekday: 'short',
                                        day: 'numeric',
                                        month: 'short'
                                    })}
                                </div>
                                <div className="text-xs opacity-75 mb-2">
                                    {date.toLocaleTimeString('en-GB', {
                                        hour: '2-digit',
                                        minute: '2-digit'
                                    })}
                                </div>

                                {isBooked ? (
                                    <div className="text-xs font-medium text-green-400 flex items-center justify-center">
                                        <CheckCircleIcon className="h-3 w-3 mr-1" />
                                        Paid
                                    </div>
                                ) : isPending ? (
                                    <div className="text-xs font-medium text-yellow-400 flex items-center justify-center">
                                        {isSelected ? (
                                            <>
                                                <CheckCircleIcon className="h-3 w-3 mr-1" />
                                                Retry
                                            </>
                                        ) : (
                                            <>
                                                <ExclamationTriangleIcon className="h-3 w-3 mr-1" />
                                                Pending
                                            </>
                                        )}
                                    </div>
                                ) : isSelected ? (
                                    <div className="text-xs font-medium flex items-center justify-center">
                                        <CheckCircleIcon className="h-3 w-3 mr-1" />
                                        Selected
                                    </div>
                                ) : (
                                    <div className="text-xs text-emerald-400 font-medium">
                                        £{venueClass.price}
                                    </div>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* User's Booked Sessions */}
            {showUserBookings && venueClass.userClasses && venueClass.userClasses.length > 0 && (
                <div className="mt-6 bg-gray-800/50 border border-gray-600 rounded-lg p-4">
                    <h5 className="text-md font-semibold text-white mb-3">Your Upcoming Sessions</h5>
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-3">
                        {venueClass.userClasses
                            .filter(booking => {
                                const bookingDate = new Date(booking.paymentDate);
                                const now = new Date();
                                const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
                                return bookingDate >= todayStart;
                            })
                            .sort((a, b) => new Date(a.paymentDate) - new Date(b.paymentDate))
                            .map((booking, index) => {
                                const bookingDate = new Date(booking.paymentDate);
                                const isPaid = booking.hasPaid;

                                return (
                                    <div
                                        key={index}
                                        className={`p-3 rounded-lg border ${
                                            isPaid
                                                ? 'bg-green-900/20 border-green-600'
                                                : 'bg-yellow-900/20 border-yellow-600'
                                        }`}
                                    >
                                        <div className="text-center">
                                            <div className="font-semibold text-sm mb-1 text-gray-200">
                                                {bookingDate.toLocaleDateString('en-GB', {
                                                    weekday: 'short',
                                                    day: 'numeric',
                                                    month: 'short'
                                                })}
                                            </div>
                                            <div className="text-xs opacity-75 mb-2 text-gray-300">
                                                {bookingDate.toLocaleTimeString('en-GB', {
                                                    hour: '2-digit',
                                                    minute: '2-digit'
                                                })}
                                            </div>
                                            <div className={`text-xs font-medium flex items-center justify-center ${
                                                isPaid ? 'text-green-400' : 'text-yellow-400'
                                            }`}>
                                                {isPaid ? (
                                                    <>
                                                        <CheckCircleIcon className="h-3 w-3 mr-1" />
                                                        Confirmed
                                                    </>
                                                ) : (
                                                    <>
                                                        <ExclamationTriangleIcon className="h-3 w-3 mr-1" />
                                                        Pending
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                    </div>
                </div>
            )}
        </div>
    );
};

export default ClassScheduleExpanded;
